/**
 * States, provinces and territories — `docs/address-state-field.md` §2.
 *
 * `properties.state` and `contacts.state` were free text, so the same place
 * arrived as "PA", "Pa.", "Pennsylvania" and "penn" depending on who typed
 * it. A State column then sorted those as four things, a group-by counted
 * them as four places, and the quick filter could not offer chips at all —
 * chips need a set, and typed text is not one.
 *
 * So the value stored is the two-letter code and nothing else, and this file
 * is the only thing that decides what a code is. One list, used by the
 * select, the list engine's enum options, the import normalizer and the zod
 * schema every address validates against — because four copies of "what is a
 * state" is how three of them end up out of date.
 */
import { z } from 'zod';
const US = [
    ['AL', 'Alabama', 'state'], ['AK', 'Alaska', 'state'], ['AZ', 'Arizona', 'state'],
    ['AR', 'Arkansas', 'state'], ['CA', 'California', 'state'], ['CO', 'Colorado', 'state'],
    ['CT', 'Connecticut', 'state'], ['DE', 'Delaware', 'state'],
    ['DC', 'District of Columbia', 'district'],
    ['FL', 'Florida', 'state'], ['GA', 'Georgia', 'state'], ['HI', 'Hawaii', 'state'],
    ['ID', 'Idaho', 'state'], ['IL', 'Illinois', 'state'], ['IN', 'Indiana', 'state'],
    ['IA', 'Iowa', 'state'], ['KS', 'Kansas', 'state'], ['KY', 'Kentucky', 'state'],
    ['LA', 'Louisiana', 'state'], ['ME', 'Maine', 'state'], ['MD', 'Maryland', 'state'],
    ['MA', 'Massachusetts', 'state'], ['MI', 'Michigan', 'state'], ['MN', 'Minnesota', 'state'],
    ['MS', 'Mississippi', 'state'], ['MO', 'Missouri', 'state'], ['MT', 'Montana', 'state'],
    ['NE', 'Nebraska', 'state'], ['NV', 'Nevada', 'state'], ['NH', 'New Hampshire', 'state'],
    ['NJ', 'New Jersey', 'state'], ['NM', 'New Mexico', 'state'], ['NY', 'New York', 'state'],
    ['NC', 'North Carolina', 'state'], ['ND', 'North Dakota', 'state'], ['OH', 'Ohio', 'state'],
    ['OK', 'Oklahoma', 'state'], ['OR', 'Oregon', 'state'], ['PA', 'Pennsylvania', 'state'],
    ['RI', 'Rhode Island', 'state'], ['SC', 'South Carolina', 'state'],
    ['SD', 'South Dakota', 'state'], ['TN', 'Tennessee', 'state'], ['TX', 'Texas', 'state'],
    ['UT', 'Utah', 'state'], ['VT', 'Vermont', 'state'], ['VA', 'Virginia', 'state'],
    ['WA', 'Washington', 'state'], ['WV', 'West Virginia', 'state'],
    ['WI', 'Wisconsin', 'state'], ['WY', 'Wyoming', 'state'],
    ['AS', 'American Samoa', 'territory'], ['GU', 'Guam', 'territory'],
    ['MP', 'Northern Mariana Islands', 'territory'], ['PR', 'Puerto Rico', 'territory'],
    ['VI', 'U.S. Virgin Islands', 'territory'],
];
const CA = [
    ['AB', 'Alberta', 'province'], ['BC', 'British Columbia', 'province'],
    ['MB', 'Manitoba', 'province'], ['NB', 'New Brunswick', 'province'],
    ['NL', 'Newfoundland and Labrador', 'province'], ['NS', 'Nova Scotia', 'province'],
    ['NT', 'Northwest Territories', 'territory'], ['NU', 'Nunavut', 'territory'],
    ['ON', 'Ontario', 'province'], ['PE', 'Prince Edward Island', 'province'],
    ['QC', 'Quebec', 'province'], ['SK', 'Saskatchewan', 'province'],
    ['YT', 'Yukon', 'territory'],
];
const build = (rows, country) => rows.map(([code, name, kind]) => ({ code, name, country, kind }))
    .sort((a, b) => a.name.localeCompare(b.name));
export const REGIONS_BY_COUNTRY = {
    US: build(US, 'US'),
    CA: build(CA, 'CA'),
};
/** Every region, US first. Alphabetical by name within a country. */
export const REGIONS = [...REGIONS_BY_COUNTRY.US, ...REGIONS_BY_COUNTRY.CA];
/**
 * A code to its region.
 *
 * Not scoped to a country: two of them collide (`NS` is only Canadian, but
 * `PE`, `NT` and `NU` have no US twin either way), and the stored value has
 * to resolve whatever country the row says it is in — a row whose country was
 * changed by mistake should still read as a place rather than blank.
 */
export function regionByCode(code) {
    const wanted = code.trim().toUpperCase();
    return REGIONS.find((region) => region.code === wanted);
}
/**
 * The abbreviations people actually write, which are not prefixes.
 *
 * A prefix rule gets "Penn" → Pennsylvania on its own, but not "Calif" (the
 * name has no `f` at that position in any other state, yet "Calif" is still a
 * prefix of exactly one — so that one is free) and not "N.Y.", which is a
 * code with dots. What is listed here is what a prefix cannot reach: the
 * dotted codes, and "Mass" and "Conn" and the rest that are ambiguous only
 * because a longer name shares their start.
 */
const ALIASES = {
    PENN: 'PA', PENNA: 'PA', MASS: 'MA', CONN: 'CT', CALIF: 'CA', FLA: 'FL',
    TEX: 'TX', WASH: 'WA', MICH: 'MI', MINN: 'MN', WISC: 'WI', TENN: 'TN',
    ORE: 'OR', ARIZ: 'AZ', COLO: 'CO', OKLA: 'OK', NEB: 'NE', NEBR: 'NE',
    KAN: 'KS', KANS: 'KS', ILL: 'IL', IND: 'IN', DEL: 'DE', MISS: 'MS',
    MONT: 'MT', NEV: 'NV', VA: 'VA', WVA: 'WV', WVIRGINIA: 'WV',
    WASHINGTONDC: 'DC', DISTRICTOFCOLUMBIA: 'DC', WASHDC: 'DC',
    PUERTORICO: 'PR', VIRGINISLANDS: 'VI', USVIRGINISLANDS: 'VI',
    QUE: 'QC', ONT: 'ON', ALTA: 'AB', SASK: 'SK', MAN: 'MB', NFLD: 'NL',
    NEWFOUNDLAND: 'NL', PEI: 'PE',
};
/** Case, spaces, dots and punctuation gone — "N.Y." and "n y" are one key. */
const key = (input) => input.toUpperCase().replace(/[^A-Z]/g, '');
/**
 * Free text to a code, or null.
 *
 * The order is deliberate: an exact code, then an exact name, then the alias
 * table, then an unambiguous prefix of a name three letters or longer. It
 * **never guesses between two matches** — "New" is New Hampshire, New Jersey,
 * New Mexico and New York, and picking one of those silently is how a
 * property ends up in the wrong state with nobody ever told.
 *
 * Three letters because two is a code: "ARK" should be Arkansas, but "AR"
 * already is, and letting two-letter prefixes through would make "IN" match
 * Indiana *and* Iowa's neighbours by prefix before the code rule ever ran.
 */
export function normalizeRegion(input, country = 'US') {
    const wanted = key(input);
    if (wanted.length === 0)
        return null;
    const list = REGIONS_BY_COUNTRY[country];
    const byCode = list.find((region) => region.code === wanted);
    if (byCode)
        return byCode.code;
    const byName = list.find((region) => key(region.name) === wanted);
    if (byName)
        return byName.code;
    const alias = ALIASES[wanted];
    if (alias && list.some((region) => region.code === alias))
        return alias;
    if (wanted.length >= 3) {
        const prefixed = list.filter((region) => key(region.name).startsWith(wanted));
        if (prefixed.length === 1)
            return prefixed[0].code;
    }
    return null;
}
/**
 * What a stored state must be.
 *
 * The refine rather than a bare two-letter check: "XX" is two letters and is
 * not a place, and an address that stored it would sort and group as one.
 *
 * Upper-case only, which `regionByCode` is deliberately not. That one is a
 * reader — a row that somehow holds "pa" should still read as Pennsylvania —
 * and this is the gate on the way in, where the database's own
 * `state ~ '^[A-Z]{2}$'` is the same rule. Accepting "pa" here would turn a
 * message somebody can act on into a constraint violation three layers down.
 */
export const regionCode = z.string().length(2).refine((code) => code === code.toUpperCase() && Boolean(regionByCode(code)), { message: 'Pick a state.' });
/** "PA · Pennsylvania", for anywhere that prints one rather than drawing it. */
export function regionLabel(code) {
    const region = regionByCode(code);
    return region ? `${region.code} · ${region.name}` : code;
}
/** What the field is called where it is being filled in. */
export const regionFieldLabel = (country) => (country === 'CA' ? 'Province' : 'State');
//# sourceMappingURL=regions.js.map